/** Live reads. Every screen renders from these; none awaits the network. */

import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo } from 'react'

import { db, type CustomFoodRow, type FoodRow, type ProfileRow, type RecipeRow, type SavedMealRow,
  type TargetRow, type WeighinRow, type WorkoutRow } from '.'
import { addDays } from '../lib/days'
import { dayOf, todayIso } from '../lib/day'
import { buildModel, liveTargets, targetFor, type Model } from '../lib/model'
import type { Nutrients } from '../lib/nutrients'

const live = <T extends { deleted_at?: string | null }>(rows: T[]) => rows.filter((r) => !r.deleted_at)

export function useFoodsForDay(day: string): FoodRow[] | undefined {
  return useLiveQuery(async () => live(await db.foods.where('day').equals(day).toArray())
    .sort((a, b) => a.meal_slot - b.meal_slot || a.order_index - b.order_index), [day])
}

export function useDayStatus(day: string) {
  return useLiveQuery(async () => live(await db.day_status.where('day').equals(day).toArray())[0] ?? null, [day])
}

export function useProfile(): ProfileRow | null | undefined {
  return useLiveQuery(async () => live(await db.profiles.toArray())[0] ?? null, [])
}

export function useTargets(): TargetRow[] | undefined {
  return useLiveQuery(async () => liveTargets(await db.targets.toArray()), [])
}

export function useTargetFor(day: string): TargetRow | null | undefined {
  const t = useTargets()
  return t === undefined ? undefined : targetFor(t, day)
}

export function useWeighins(): WeighinRow[] | undefined {
  return useLiveQuery(async () => live(await db.weighins.orderBy('measured_at').toArray())
    .filter((w) => w.weight_kg), [])
}

export function useCustomFoods(): CustomFoodRow[] | undefined {
  return useLiveQuery(async () => live(await db.custom_foods.toArray())
    .sort((a, b) => (b.last_used_at ?? b.updated_at).localeCompare(a.last_used_at ?? a.updated_at)), [])
}

export function useRecipes(): RecipeRow[] | undefined {
  return useLiveQuery(async () => live(await db.recipes.toArray()).sort((a, b) => a.name.localeCompare(b.name)), [])
}

export function useSavedMeals(): SavedMealRow[] | undefined {
  return useLiveQuery(async () => live(await db.saved_meals.toArray()).sort((a, b) => a.name.localeCompare(b.name)), [])
}

export type RecentFood = {
  key: string; name: string; brand?: string | null; source: FoodRow['source']; source_ref?: string | null
  per100?: Nutrients | null; nutrients?: Nutrients | null; grams?: number | null
  serving_label?: string | null; serving_qty?: number | null; count: number; last: string
}

/** Foods logged in the last 90 days, most frequent-and-recent first. Derived
 *  from the log itself, so there is no use counter to lose in a sync race. */
export function useRecentFoods(): RecentFood[] | undefined {
  return useLiveQuery(async () => {
    const since = addDays(todayIso(), -90)
    const rows = live(await db.foods.where('day').aboveOrEqual(since).toArray())
    const m = new Map<string, RecentFood>()
    for (const r of rows) {
      if (r.source === 'quick') continue
      const key = r.source_ref ? `${r.source}:${r.source_ref}` : `name:${r.name.toLowerCase()}|${r.brand ?? ''}`
      const e = m.get(key)
      if (!e) {
        m.set(key, { key, name: r.name, brand: r.brand, source: r.source, source_ref: r.source_ref,
          per100: r.per100, nutrients: r.nutrients, grams: r.grams, serving_label: r.serving_label,
          serving_qty: r.serving_qty, count: 1, last: r.updated_at })
      } else {
        e.count++
        if (r.updated_at > e.last) {
          Object.assign(e, { last: r.updated_at, grams: r.grams, serving_label: r.serving_label,
            serving_qty: r.serving_qty, per100: r.per100 ?? e.per100, nutrients: r.nutrients })
        }
      }
    }
    const now = Date.now()
    const score = (f: RecentFood) => f.count / (1 + (now - Date.parse(f.last)) / 86_400_000 / 7)
    return [...m.values()].sort((a, b) => score(b) - score(a))
  }, [])
}

/** The whole derived model. Recomputed when any input table changes. */
export function useModel(): Model | undefined {
  const data = useLiveQuery(async () => {
    const [foods, statuses, weighins, targets, profiles, workouts] = await Promise.all([
      db.foods.toArray(), db.day_status.toArray(), db.weighins.toArray(),
      db.targets.toArray(), db.profiles.toArray(), db.workouts.toArray(),
    ])
    return { foods, statuses, weighins, targets, workouts, profile: live(profiles)[0] ?? null }
  }, [])
  const today = todayIso()
  return useMemo(() => {
    if (!data) return undefined
    return buildModel({
      today,
      foods: data.foods,
      statuses: data.statuses,
      weighins: live(data.weighins).filter((w) => w.weight_kg)
        .map((w) => ({ day: dayOf(w.measured_at), kg: w.weight_kg as number })),
      targets: data.targets,
      profile: data.profile,
      workouts: data.workouts,
    })
  }, [data, today])
}

export function useWorkoutsForDay(day: string): WorkoutRow[] | undefined {
  return useLiveQuery(async () => live(await db.workouts.where('day').equals(day).toArray())
    .sort((a, b) => (a.started_at ?? '').localeCompare(b.started_at ?? '')), [day])
}
